import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FiDollarSign, FiArrowRight, FiFileText, FiCheckCircle, FiClock,
  FiPieChart, FiUsers, FiTrendingUp, FiAlertCircle, FiBarChart2
} from 'react-icons/fi';
import PublicPageHeader from '../../../components/common/PublicPageHeader';
import PublicPageFooter from '../../../components/common/PublicPageFooter';
import DocsSidebar from '../../../components/docs/DocsSidebar';

const OrganizationFinance = () => {
  const features = [
    {
      icon: FiPieChart,
      title: 'Department Budgets',
      description: 'Allocate and track budgets for each department with real-time spending insights.'
    },
    {
      icon: FiFileText,
      title: 'Budget Requests',
      description: 'Submit, review, and approve budget requests with a streamlined workflow.'
    },
    {
      icon: FiUsers,
      title: 'Employee Finance',
      description: 'Track employee-related expenses, reimbursements, and allocations.'
    },
    {
      icon: FiTrendingUp,
      title: 'Finance Analytics',
      description: 'Comprehensive analytics and reports for financial decision-making.'
    }
  ];

  const requestStatuses = [
    { status: 'Pending', color: 'bg-yellow-100 text-yellow-800', description: 'Awaiting review' },
    { status: 'Approved', color: 'bg-green-100 text-green-800', description: 'Request accepted' },
    { status: 'Rejected', color: 'bg-red-100 text-red-800', description: 'Request declined' },
    { status: 'In Review', color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800', description: 'Being evaluated' }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <PublicPageHeader />
      
      <div className="flex">
        <DocsSidebar currentPath="/docs/organization/finance" />
        
        <main className="flex-1 max-w-4xl mx-auto px-6 py-12">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 mb-8">
            <Link to="/docs" className="hover:text-blue-600">Docs</Link>
            <span>/</span>
            <Link to="/docs/organization" className="hover:text-blue-600">Organization Workspace</Link>
            <span>/</span>
            <span className="text-gray-900 dark:text-white">Finance & Budgets</span>
          </nav>

          {/* Header */}
          <div className="mb-12">
            <div className="inline-flex p-2 bg-green-100 dark:bg-green-900/30 rounded-xl mb-4">
              <FiDollarSign className="w-6 h-6 text-green-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Finance & Budget Management</h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Comprehensive financial management for organizations with department budgets, 
              request workflows, and detailed analytics.
            </p>
          </div>

          {/* Overview */}
          <section className="mb-16">
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Finance Dashboard Overview</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                The Organization Finance module provides a centralized hub for managing all 
                financial aspects of your organization, from department budgets to expense tracking.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                {features.map((feature, index) => {
                  const Icon = feature.icon;
                  return (
                    <div key={index} className="bg-white dark:bg-gray-800 rounded-xl p-4">
                      <div className="flex items-center space-x-3 mb-2">
                        <Icon className="w-5 h-5 text-green-600" />
                        <span className="font-medium text-gray-900 dark:text-white">{feature.title}</span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-300">{feature.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Department Budgets */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Department Budgets</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Allocate budgets to departments and track spending in real-time:
            </p>

            <div className="space-y-4">
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Creating a Department Budget</h3>
                <ol className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                  <li className="flex items-start space-x-2">
                    <span className="text-green-600 font-medium">1.</span>
                    <span>Navigate to Organization Finance → Department Budgets</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-green-600 font-medium">2.</span>
                    <span>Click "Create Budget" for a department</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-green-600 font-medium">3.</span>
                    <span>Set budget amount, period (monthly/quarterly/yearly), and categories</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-green-600 font-medium">4.</span>
                    <span>Configure alerts for spending thresholds</span>
                  </li>
                </ol>
              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Budget Tracking</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                  Monitor budget utilization with real-time updates:
                </p>
                <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
                  <li className="flex items-center space-x-2">
                    <FiCheckCircle className="w-4 h-4 text-green-500" />
                    <span>Total allocated vs. spent</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <FiCheckCircle className="w-4 h-4 text-green-500" />
                    <span>Spending by category breakdown</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <FiCheckCircle className="w-4 h-4 text-green-500" />
                    <span>Trend analysis over time</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <FiCheckCircle className="w-4 h-4 text-green-500" />
                    <span>Forecasting and projections</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Budget Requests */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Budget Requests</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Submit and manage budget requests with an approval workflow:
            </p>

            <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-6 mb-6">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Request Workflow</h3>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm">1</div>
                  <span className="text-sm text-gray-700 dark:text-gray-200">Submit</span>
                </div>
                <div className="flex-1 h-0.5 bg-gray-300 mx-2"></div>
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center text-white text-sm">2</div>
                  <span className="text-sm text-gray-700 dark:text-gray-200">Review</span>
                </div>
                <div className="flex-1 h-0.5 bg-gray-300 mx-2"></div>
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white text-sm">3</div>
                  <span className="text-sm text-gray-700 dark:text-gray-200">Approve</span>
                </div>
                <div className="flex-1 h-0.5 bg-gray-300 mx-2"></div>
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-white text-sm">4</div>
                  <span className="text-sm text-gray-700 dark:text-gray-200">Allocate</span>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Request Statuses</h3>
              <div className="flex flex-wrap gap-3">
                {requestStatuses.map((item, index) => (
                  <div key={index} className={`px-3 py-2 rounded-lg ${item.color}`}>
                    <span className="font-medium">{item.status}</span>
                    <span className="text-xs ml-2">- {item.description}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-100 dark:border-blue-800 rounded-xl p-4">
              <div className="flex items-start space-x-3">
                <FiAlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-900 dark:text-blue-300">Who Can Approve?</h4>
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    Budget requests are typically approved by Department Managers, Finance Managers, 
                    or Organization Admins depending on the request amount and your organization's 
                    approval matrix.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Finance Analytics */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Finance Analytics</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Gain insights with comprehensive financial analytics:
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <div className="flex items-center space-x-3 mb-3">
                  <FiBarChart2 className="w-5 h-5 text-purple-600" />
                  <h3 className="font-semibold text-gray-900 dark:text-white">Budget Overview</h3>
                </div>
                <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                  <li>• Total organization budget</li>
                  <li>• Department-wise allocation</li>
                  <li>• Utilization percentages</li>
                  <li>• Remaining funds</li>
                </ul>
              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <div className="flex items-center space-x-3 mb-3">
                  <FiTrendingUp className="w-5 h-5 text-green-600" />
                  <h3 className="font-semibold text-gray-900 dark:text-white">Spending Trends</h3>
                </div>
                <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                  <li>• Monthly spending patterns</li>
                  <li>• Year-over-year comparison</li>
                  <li>• Category breakdown</li>
                  <li>• Forecast projections</li>
                </ul>
              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <div className="flex items-center space-x-3 mb-3">
                  <FiUsers className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-gray-900 dark:text-white">Employee Expenses</h3>
                </div>
                <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                  <li>• Per-employee costs</li>
                  <li>• Reimbursement tracking</li>
                  <li>• Department headcount costs</li>
                  <li>• Cost per project</li>
                </ul>
              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <div className="flex items-center space-x-3 mb-3">
                  <FiFileText className="w-5 h-5 text-orange-600" />
                  <h3 className="font-semibold text-gray-900 dark:text-white">Reports</h3>
                </div>
                <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                  <li>• Custom report builder</li>
                  <li>• Scheduled reports</li>
                  <li>• Export to PDF/Excel</li>
                  <li>• Audit trail reports</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Permissions */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Finance Permissions</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Different roles have different finance access levels:
            </p>

            <div className="overflow-hidden border border-gray-200 dark:border-gray-700 rounded-xl">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">Role</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900 dark:text-white">View</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900 dark:text-white">Request</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900 dark:text-white">Approve</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900 dark:text-white">Configure</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  <tr>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">Org Admin</td>
                    <td className="px-4 py-3 text-center"><FiCheckCircle className="inline text-green-500" /></td>
                    <td className="px-4 py-3 text-center"><FiCheckCircle className="inline text-green-500" /></td>
                    <td className="px-4 py-3 text-center"><FiCheckCircle className="inline text-green-500" /></td>
                    <td className="px-4 py-3 text-center"><FiCheckCircle className="inline text-green-500" /></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">Department Manager</td>
                    <td className="px-4 py-3 text-center"><FiCheckCircle className="inline text-green-500" /></td>
                    <td className="px-4 py-3 text-center"><FiCheckCircle className="inline text-green-500" /></td>
                    <td className="px-4 py-3 text-center"><FiCheckCircle className="inline text-green-500" /></td>
                    <td className="px-4 py-3 text-center text-gray-400 dark:text-gray-500">—</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">Team Lead</td>
                    <td className="px-4 py-3 text-center"><FiCheckCircle className="inline text-green-500" /></td>
                    <td className="px-4 py-3 text-center"><FiCheckCircle className="inline text-green-500" /></td>
                    <td className="px-4 py-3 text-center text-gray-400 dark:text-gray-500">—</td>
                    <td className="px-4 py-3 text-center text-gray-400 dark:text-gray-500">—</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">Employee</td>
                    <td className="px-4 py-3 text-center text-gray-400 dark:text-gray-500">Own only</td>
                    <td className="px-4 py-3 text-center"><FiCheckCircle className="inline text-green-500" /></td>
                    <td className="px-4 py-3 text-center text-gray-400 dark:text-gray-500">—</td>
                    <td className="px-4 py-3 text-center text-gray-400 dark:text-gray-500">—</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Navigation */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Related Guides</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Link
                to="/docs/organization/departments"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-purple-50 dark:hover:bg-purple-900/20 border border-transparent hover:border-purple-200 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Department Management</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
              <Link
                to="/docs/admin/roles"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-red-50 border border-transparent hover:border-red-200 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Role Permissions</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
            </div>
          </section>

          {/* Article Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Last updated: January 27, 2026
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">Was this helpful?</span>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                  Yes
                </button>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                  No
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>

      <PublicPageFooter />
    </div>
  );
};

export default OrganizationFinance;
